/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AccountConnection, AccountOrder, CompanyVerificationStatus, MutationStatus, TenantAccount } from '../../gql-types.generated';
import { RootState } from '../../store';

interface SliceState {
  error?: Error;
  loading: boolean;
  tenantsConnection?: AccountConnection;
  tenantList: TenantAccount[];
  totalCount: number;
  orderBy?: AccountOrder;
  queryString?: string;
  statusFilter?: CompanyVerificationStatus[];
  deleteStatus?: MutationStatus;
  deleteError?: Error;
}

const initialState: SliceState = {
  error: undefined,
  loading: true,
  tenantsConnection: undefined,
  tenantList: [],
  totalCount: 0,
  orderBy: undefined,
  queryString: undefined,
  statusFilter: undefined,
  deleteStatus: undefined,
  deleteError: undefined,
};

export const slice = createSlice({
  name: 'allTenants',
  initialState,
  reducers: {
    fetchAllTenantsStart: state => {
      state.loading = true;
      state.error = undefined;
    },
    fetchError: (state, action: PayloadAction<Error>) => {
      state.error = action.payload;
      state.loading = false;
    },
    fetchAllTenantsSuccess: (state, action: PayloadAction<AccountConnection>) => {
      state.tenantsConnection = action.payload;
      let items = state.tenantList;

      // Reset on first page load; append on subsequent pages (scroll).
      if (!items || !state.tenantsConnection.pageInfo.hasPreviousPage) {
        items = [];
      }

      action.payload.nodes?.filter(Boolean).forEach(node => {
        if (!items?.some(i => i?.id === node?.id)) {
          items?.push(node as TenantAccount);
        }
      });

      state.tenantList = items;
      state.totalCount = action.payload.totalCount ?? 0;
      state.error = undefined;
      state.loading = false;
    },
    captureOrderBy: (state, action: PayloadAction<AccountOrder | undefined>) => {
      state.loading = true;
      state.orderBy = action.payload;
    },
    captureQueryString: (state, action: PayloadAction<string | undefined>) => {
      state.loading = true;
      state.queryString = action.payload;
    },
    captureStatusFilter: (state, action: PayloadAction<CompanyVerificationStatus | undefined>) => {
      state.loading = true;
      state.statusFilter = action.payload ? [action.payload] : undefined;
    },
    captureDeleteStatus: (state, action: PayloadAction<MutationStatus | undefined>) => {
      state.deleteStatus = action.payload;
    },
    captureDeleteError: (state, action: PayloadAction<Error | undefined>) => {
      state.deleteError = action.payload;
    },
  },
});

export const selectAllTenants = (state: RootState): TenantAccount[] => state.allTenants.tenantList;
export const selectTenantsConnection = (state: RootState): AccountConnection | undefined => state.allTenants.tenantsConnection;
export const selectAllTenantsError = (state: RootState): Error | undefined => state.allTenants.error;
export const selectAllTenantsLoading = (state: RootState): boolean => state.allTenants.loading;
export const selectAllTenantsTotalCount = (state: RootState): number => state.allTenants.totalCount;
export const selectAllTenantsOrderBy = (state: RootState): AccountOrder | undefined => state.allTenants.orderBy;
export const selectAllTenantsQueryString = (state: RootState): string | undefined => state.allTenants.queryString;
export const selectAllTenantsStatusFilter = (state: RootState): CompanyVerificationStatus[] | undefined =>
  state.allTenants.statusFilter;
export const selectDeleteTenantStatus = (state: RootState): MutationStatus | undefined => state.allTenants.deleteStatus;
export const selectDeleteTenantError = (state: RootState): Error | undefined => state.allTenants.deleteError;

export const {
  fetchAllTenantsStart,
  fetchError,
  fetchAllTenantsSuccess,
  captureOrderBy,
  captureQueryString,
  captureStatusFilter,
  captureDeleteStatus,
  captureDeleteError,
} = slice.actions;

export default slice.reducer;
